import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function CountdownTimer() {
  const getTimeRemaining = () => {
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    const diff = endOfDay.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { hours, minutes, seconds };
  };

  const [time, setTime] = useState(getTimeRemaining);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeRemaining());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n: number) => n.toString().padStart(2, '0');

  return (
    <div className="flex items-center gap-1.5">
      {[
        { value: pad(time.hours), label: 'HRS' },
        { value: pad(time.minutes), label: 'MIN' },
        { value: pad(time.seconds), label: 'SEC' },
      ].map((unit, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-2.5 py-1.5 min-w-[44px] text-center">
            <span className="text-white font-bold text-lg leading-none block">{unit.value}</span>
            <span className="text-white/60 text-[9px] font-semibold tracking-wider">{unit.label}</span>
          </div>
          {i < 2 && <span className="text-white/50 font-bold text-lg">:</span>}
        </div>
      ))}
    </div>
  );
}

export default function Pricing() {
  const plans = [
    {
      name: 'PROFESSIONAL',
      price: '297',
      description: 'Most popular for growing teams',
      setupFee: '1,000',
      setupFeeDiscounted: '500',
      showScratch: true,
      features: [
        { text: 'Up to 500 calls/month', included: true },
        { text: 'Advanced scheduling & reminders', included: true },
        { text: 'Priority email & chat support', included: true },
        { text: '30+ languages supported', included: true },
        { text: 'Advanced analytics & reports', included: true },
        { text: 'CRM integration', included: true },
        { text: 'Custom voice training', included: true },
        { text: 'Priority support', included: true }
      ],
      popular: true
    },
    {
      name: 'ENTERPRISE',
      price: '497',
      description: 'For large organizations',
      setupFee: '1,500',
      setupFeeDiscounted: null,
      showScratch: false,
      features: [
        { text: 'Unlimited calls', included: true },
        { text: 'Enterprise scheduling suite', included: true },
        { text: '24/7 dedicated support', included: true },
        { text: 'All languages supported', included: true },
        { text: 'Custom analytics dashboard', included: true },
        { text: 'Full CRM integration', included: true },
        { text: 'Custom voice & personality', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Smart website build', included: true },
        { text: 'Google review optimization', included: true }
      ],
      popular: false
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-6">
        {/* Limited Time Offer Banner */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-rose-600 via-red-500 to-orange-500 p-[1px]">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-rose-600 via-red-500 to-orange-500 px-6 py-5">
              {/* Animated shimmer */}
              <div className="absolute inset-0 opacity-20">
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 45%, rgba(255,255,255,0.4) 50%, transparent 55%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s infinite linear',
                  }}
                />
              </div>

              <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl shrink-0 animate-pulse">
                    <i className="ri-fire-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-white font-bold text-base">LIMITED TIME OFFER</span>
                      <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-[10px] font-extrabold rounded-full whitespace-nowrap">SAVE $500</span>
                    </div>
                    <p className="text-white/80 text-sm">
                      Professional setup fee slashed from <span className="line-through decoration-white/60">$1,000</span> to just <strong className="text-white">$500</strong> — don&apos;t miss out!
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="text-white/60 text-[10px] font-semibold tracking-wider uppercase">Offer ends in</p>
                  </div>
                  <CountdownTimer />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-4">
            Choose Your Perfect Plan
          </h2>
          <p className="text-xl text-gray-400">
            Flexible pricing that grows with your business. Cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`relative bg-white rounded-3xl p-8 transition-all duration-300 hover:shadow-2xl ${
                plan.popular ? 'md:scale-105 shadow-2xl border-4 border-purple-500' : 'shadow-lg'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold rounded-full shadow-lg whitespace-nowrap">
                    MOST POPULAR
                  </div>
                </div>
              )}

              {/* Plan Name */}
              <div className="text-center mb-6 mt-4">
                <h3 className="text-sm font-bold text-gray-500 tracking-wider mb-2">
                  {plan.name}
                </h3>
                
                {/* Price */}
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-lg text-gray-500 ml-2">/month</span>
                </div>
                
                {/* Setup Fee */}
                <div className="mt-3 mb-1">
                  {plan.showScratch ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm text-gray-500">Setup fee:</span>
                      <span className="relative text-sm font-semibold text-gray-400">
                        <span className="line-through decoration-red-500 decoration-2">$1,000</span>
                      </span>
                      <span className="text-sm font-bold text-green-600">${plan.setupFeeDiscounted}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm text-gray-500">Setup fee:</span>
                      <span className="text-sm font-bold text-gray-900">${plan.setupFee}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600">{plan.description}</p>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-6"></div>

              {/* Features List */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${
                      feature.included ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <i className={`${feature.included ? 'ri-check-line text-green-600' : 'ri-close-line text-gray-400'} text-sm`}></i>
                    </div>
                    <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Link 
                to="/start-free-trial"
                className={`block w-full py-4 text-center font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  plan.popular
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-xl'
                    : 'border-2 border-gray-300 text-gray-700 hover:border-purple-600 hover:text-purple-600'
                }`}
              >
                Get Started
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12">
          <p className="text-gray-400 text-sm">
            All plans include a 7-day free trial.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </section>
  );
}
